import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useShopifyApi from '../hooks/useShopifyApi';
import WarningCircle from '../assets/img/warning-circle.svg';
import aaronsLogo from '../assets/img/aarons-logo.svg';
import flexiPayIcon from '../assets/img/flexi-pay-icon.png';
import autopayIcon from '../assets/img/autopay-icon.png';
import deliveryIcon from '../assets/img/delivery-icon.png';
import { isRenderableApproval, toAmount, formatMonthly, formatCashPrice } from '../helpers/aaronsReferral';

// eslint-disable-next-line no-shadow
function NotApproved({ url }) {
  const store = useSelector(state => ({
    shopify: state.technical?.shopify,
    checkoutLookupData: state.personal?.checkoutLookupData,
    aarons: state.personal?.aarons,
    isInstoreVccFlow: state.technical?.instoreVccFlow?.isInstoreVccFlow,
  }));
  const { request, response } = useShopifyApi();

  // This reject is unconditional and fires on mount on BOTH the standard
  // decline and the Aaron's referral, with the same reject_reason on both
  // paths. Do not make it conditional on the referral: the Shopify order is
  // genuinely dead either way, and a single component with one useEffect
  // cannot forget to reject it. See spec/aarons-decline-flow/SPEC.md §5 §6.
  useEffect(() => {
    // The reject endpoint is Shopify-specific; the in-store VCC flow has no transaction to reject.
    if (store.isInstoreVccFlow) {
      return;
    }
    const rejectRequest = async () => {
      await request.rejectRequest({
        transaction_id: store.shopify?.transactionId,
        reject_reason: 'APPLICATION_DECLINED',
      });
      await response?.json();
    };
    rejectRequest();
  }, []);

  const goBackToRetailor = () => {
    window.location.href = url;
  };

  const aarons = store.aarons;
  const showAaronsReferral = isRenderableApproval(aarons);
  const monthlyAmount = toAmount(aarons?.approval_amount);
  const cashPriceAmount = toAmount(aarons?.example_cash_price);
  const ctaUrl = aarons?.cta_url;

  const goToAarons = () => {
    // 1. window.open must run synchronously in this click handler, with no
    // await ahead of it and before the window.location assignment below.
    // Reverse that order and the pending navigation can cancel the open;
    // put any async work first and iOS Safari treats the popup as
    // script-initiated rather than user-initiated, and blocks it.
    const opened = window.open(ctaUrl, '_blank');
    if (opened) {
      // 2. Never pass 'noopener' to window.open: with that feature string it
      // returns null by spec, indistinguishable from a blocked popup.
      // Opening plainly and then clearing opener here gets both a severed
      // reference and a return value we can branch on.
      opened.opener = null;
      window.location.href = url; // back to Shopify
    } else {
      // 3. Popup blocked — send the top-level window to ctaUrl, not to url.
      // The Shopify session is already rejected either way; this at least
      // delivers what the screen promised instead of stranding the customer.
      window.location.href = ctaUrl;
    }
  };

  if (showAaronsReferral) {
    return (
      <div className="k-content-wrapper">
        <section className="k-info k-aarons-referral" data-testid="aarons-referral">
          <div className="k-aarons-warning-icon-wrapper">
            <img className="k-aarons-warning-icon" src={WarningCircle} alt="" />
          </div>
          <h1 className="congratulation-header">We are unable to approve you at this time</h1>
          <p className="k-not-approved-text">
            While Katapult is unable to approve your application right now, our trusted partner, Aaron&apos;s, may be
            able to help.
          </p>

          <div className="k-aarons-offer">
            <div className="k-aarons-offer-header">
              <img className="k-aarons-logo" data-testid="aarons-logo" src={aaronsLogo} alt="Aaron's" />
              <span className="k-aarons-badge" data-testid="aarons-recommended-badge">
                Recommended for you
              </span>
            </div>
            <p className="k-aarons-description" data-testid="aarons-description">
              A trusted lease to own provider with flexible options and fast approval.
            </p>
            <p className="k-aarons-leasing-power-label" data-testid="aarons-leasing-power-label">
              Your approved Leasing Power* is
            </p>
            <p className="k-aarons-amount" data-testid="aarons-approval-amount">
              {`${formatMonthly(monthlyAmount)}/mo`}
            </p>
            {cashPriceAmount !== null && (
              <p className="k-aarons-cash-price" data-testid="aarons-cash-price">
                {`That's up to ${formatCashPrice(cashPriceAmount)} in total Leasing Power** to spend at Aaron's`}
              </p>
            )}

            <ul className="k-aarons-benefits">
              <li className="k-aarons-benefit">
                <img className="k-aarons-benefit-icon" src={flexiPayIcon} alt="" />
                <span>Flexible payments</span>
              </li>
              <li className="k-aarons-benefit">
                <img className="k-aarons-benefit-icon" src={autopayIcon} alt="" />
                <span>Pay your way with EZPay Autopay</span>
              </li>
              <li className="k-aarons-benefit">
                <img className="k-aarons-benefit-icon" src={deliveryIcon} alt="" />
                <span>Free Delivery &amp; set up</span>
              </li>
            </ul>
          </div>

          {aarons?.has_account === false && (
            <div className="k-aarons-callout" data-testid="aarons-email-callout">
              <span className="k-aarons-callout-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" focusable="false">
                  <rect x="2.5" y="5" width="19" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M3.5 6.5L12 13L20.5 6.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div className="k-aarons-callout-content">
                <h2 className="k-aarons-callout-heading">Check your email</h2>
                <p>You&apos;ll receive an email to setup your digital Aaron&apos;s account.</p>
              </div>
            </div>
          )}

          <div className="k-submit k-aarons-actions">
            <button type="button" className="k-aarons-cta" data-testid="aarons-cta" onClick={goToAarons}>
              Continue with Aaron&apos;s
            </button>
          </div>
          <button type="button" className="k-aarons-dismiss" data-testid="aarons-dismiss" onClick={goBackToRetailor}>
            Close
          </button>
          <div className="k-aarons-disclaimer" data-testid="aarons-disclaimer">
            <p>
              *Leasing Power is the maximum total monthly lease payment amount that you can use to shop at Aaron&apos;s.
              Some merchandise category exclusions apply. See Aaron&apos;s for details.
            </p>
            {cashPriceAmount !== null && (
              <p data-testid="aarons-disclaimer-leasing-power">
                **Total Leasing Power reflects 24-month lease ownership plan. Lease ownership plans may vary. Ownership
                is optional. See Aaron&apos;s for details.
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="k-content-wrapper">
      <section className="k-info" data-testid="standard-decline">
        <div className="k-error-image-wrapper">
          <img width="150px" height="150px" className="error-icon" src={WarningCircle} alt="" />
        </div>
        <h1 className="congratulation-header">Thanks for applying</h1>
        <p className="k-not-approved-text">
          Unfortunately, we are unable to approve you at this time. For questions call 833-KATAPULT(528-2785)
        </p>
        <div className="k-submit k-error-button k-not-approved-button">
          <button type="button" onClick={goBackToRetailor}>
            <strong>Close</strong>
          </button>
        </div>
      </section>
    </div>
  );
}

export default NotApproved;
